import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { buildCityService, buildBreadcrumb, buildFaqPage, ORG_ID } from '../../../../lib/structuredData';
import Header from '../../../../components/ui/Header';
import Footer from '../../../../components/Footer';
import InstantEstimateHero from '../../../homepage/components/InstantEstimateHero';
import { openCustomerPortal } from '../../../../utils/cn';
import { canonicalUrl } from '../../../../lib/seo.mjs';

const MAPBOX_TOKEN = import.meta.env?.VITE_MAPBOX_ACCESS_TOKEN;

// ─── Messaging canon (do not drift): open-book pricing with the single
// "Service & Warranty fee" (never "platform fee"; branded form is portal-only), exact GAF-measured
// quote TEXTED AND EMAILED in ~30 minutes (never "instant"), $0 down + pay after install
// and a 7-day inspection window, triple-layer warranty. Retail framing only —
// no insurance/claims language (TX §4102 guardrail). ─────────────────────────

// Trailing slash is REQUIRED — the bare path 301s (see docs/url-conventions.md).
// This const also feeds the JSON-LD @id/url and the breadcrumb item, so a
// redirecting URL here would desync the entity graph from the canonical tag.
const CANONICAL_URL = canonicalUrl('/service-areas/texas/dallas');

// Dallas-specific Service schema wording — carries the current canon (GAF-
// exclusive, "Service & Warranty fee") instead of the legacy
// national ROOF_OFFER text.
const DALLAS_SERVICE_DESCRIPTION =
  'Transparent online roof-replacement pricing for Dallas, TX homeowners — premium GAF shingles installed by vetted, GAF-certified local crews, open-book pricing with GAF materials at wholesale cost plus one clearly itemized Service & Warranty fee of about 20%, and $0 down: you pay after installation and a 7-day inspection window. Licensed in Texas with crews statewide; Dallas is the OpenbooQ Texas launch market.';

const DALLAS_OFFER = {
  '@type': 'Offer',
  name: 'Transparent online roof replacement pricing in Dallas, TX',
  description:
    'GAF roofing materials at wholesale cost plus one transparent Service & Warranty fee of about 20% — versus the 30–50% margin typical of traditional roofers. $0 down: the homeowner pays after the roof is installed, with a 7-day inspection window before payment. Exact GAF-measured quote texted and emailed in about 30 minutes. Monthly financing is available through Enhancify.',
  priceCurrency: 'USD',
  availability: 'https://schema.org/InStock',
  areaServed: { '@type': 'City', name: 'Dallas, TX' },
  seller: { '@id': ORG_ID },
  eligibleRegion: { '@type': 'State', name: 'Texas' },
};

// Visible FAQ copy — the FAQPage JSON-LD below mirrors this text 1:1 so the
// markup is always backed by on-page content (same rule as the homepage FAQ).
const DALLAS_FAQS = [
  {
    question: 'Is OpenbooQ a licensed roofing company in Texas?',
    answer:
      'Yes. OpenbooQ is licensed in Texas with vetted, GAF-certified install crews available statewide — and Dallas is our Texas launch market. We install GAF products exclusively, so every Dallas roof is a full GAF system installed by certified crews.',
  },
  {
    question: 'How fast can I get a roof replacement quote in Dallas?',
    answer:
      'Enter your address and we order a GAF aerial measurement of your exact roof — no in-home visit, no sales appointment. Your exact, GAF-measured quote is texted and emailed to you in about 30 minutes, itemized line by line.',
  },
  {
    question: 'Do I really pay $0 down for a new roof in Dallas?',
    answer:
      'Yes. You pay nothing upfront. OpenbooQ pays for your GAF materials, schedules the crew, and pulls permits before any money moves. You pay after your roof is installed — and you get a 7-day inspection window to check the finished work before payment.',
  },
  {
    question: 'What is the "Service & Warranty fee" on my quote?',
    answer:
      'OpenbooQ pricing is open-book: you see GAF materials and labor at wholesale cost, plus one clearly itemized line — the Service & Warranty fee of about 20% — that covers project management, vetted GAF-certified crews, permits, and the warranty coverage behind your roof. That single transparent line replaces the 30–50% blended margin a traditional roofer builds into a lump-sum bid.',
  },
  {
    question: 'Can my new roof stand up to Dallas hail?',
    answer:
      'Dallas sits in the heart of North Texas hail country, so we quote roofs built for it: every OpenbooQ roof is a full GAF system, and homeowners can choose the GAF Timberline AS II — a UL 2218 Class 4 impact-rated shingle engineered for storm-prone markets — for maximum hail resilience.',
  },
  {
    question: 'What warranty comes with my Dallas roof?',
    answer:
      'Triple-layer protection: a lifetime limited GAF materials warranty on the shingle system, a 5-year OpenbooQ workmanship warranty on the installation, and our satisfaction guarantee — you inspect and approve the finished roof before you pay.',
  },
  {
    question: 'Which Dallas neighborhoods and DFW suburbs does OpenbooQ serve?',
    answer:
      'All of them. We serve Dallas neighborhoods from Oak Cliff, Lakewood and Lake Highlands to Preston Hollow and the M Streets, plus Richardson, Garland, Plano, Frisco, McKinney, Arlington, Fort Worth and the wider DFW Metroplex. Because OpenbooQ is licensed statewide, any Texas address can get a quote.',
  },
];

const AddressInputWithMapbox = ({ placeholder, buttonText, buttonId, microcopy }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const fetchSuggestions = async (val) => {
    if (!val || val?.length < 3) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const res = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(val)}.json?access_token=${MAPBOX_TOKEN}&types=address&country=US&proximity=-96.7970,32.7767&autoComplete=true`
      );
      const data = await res?.json();
      setSuggestions(data?.features || []);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const val = e?.target?.value;
    setQuery(val);
    clearTimeout(debounceRef?.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelect = (place) => {
    setQuery(place?.place_name || '');
    setSuggestions([]);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    setSuggestions([]);
    // Carry the typed address into the portal so the wizard opens pre-filled.
    openCustomerPortal(query);
  };

  return (
    <div className="relative w-full">
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <div className="relative flex-1">
          {/* Location pin icon inside input */}
          <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10">
            <svg className="w-4 h-4" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <input
            type="text"
            value={query}
            onChange={handleChange}
            placeholder={placeholder}
            className="w-full pl-10 pr-5 py-4 rounded-full text-gray-800 text-sm outline-none border border-gray-200 focus:border-blue-400 bg-white"
            style={{ fontFamily: "'Inter', sans-serif", boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}
            autoComplete="off"
          />
          {suggestions?.length > 0 && (
            <div
              className="absolute top-full left-0 right-0 mt-1 bg-white rounded-2xl overflow-hidden z-50"
              style={{ boxShadow: '0 8px 32px rgba(0,1,80,0.14)', border: '1px solid rgba(7,176,233,0.15)' }}
            >
              {suggestions?.map((s) => (
                <button
                  key={s?.id}
                  type="button"
                  onClick={() => handleSelect(s)}
                  className="w-full text-left px-5 py-3 text-sm text-gray-700 hover:bg-blue-50 transition-colors duration-150 border-b border-gray-50 last:border-0"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  <span className="font-medium text-gray-900">{s?.text}</span>
                  <span className="text-gray-400 ml-1">{s?.place_name?.replace(s?.text, '')}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="submit"
          className="w-full px-8 rounded-full text-white font-bold text-sm whitespace-nowrap transition-all duration-300 hover:opacity-90 hover:shadow-lg hover:scale-105"
          style={{ backgroundColor: '#07B0E9', boxShadow: '0 4px 20px rgba(7,176,233,0.35)', fontFamily: "'Poppins', 'Inter', sans-serif", minHeight: '52px' }}
        >
          {loading ? 'Loading...' : buttonText}
        </button>
      </form>
      {microcopy && (
        <p className="text-center text-xs mt-3" style={{ color: 'rgba(255,255,255,0.65)', fontFamily: "'Inter', sans-serif" }}>
          {microcopy}
        </p>
      )}
      {/* Hidden Demand IQ trigger */}
      <button id={buttonId} className="hidden" aria-hidden="true" />
    </div>
  );
};

const FaqItem = ({ question, answer, isOpen, onToggle }) => (
  <div
    className="bg-white rounded-2xl overflow-hidden"
    style={{
      boxShadow: isOpen
        ? '0 12px 32px rgba(7,176,233,0.12), 0 2px 8px rgba(0,1,80,0.06)'
        : '0 4px 24px rgba(0,1,80,0.07), 0 1px 4px rgba(0,0,0,0.04)',
      border: isOpen ? '1px solid rgba(7,176,233,0.25)' : '1px solid rgba(0,0,0,0.04)',
      transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
    }}
  >
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      className="w-full flex items-center justify-between gap-4 text-left px-5 sm:px-7 py-5"
    >
      <span
        className="font-bold text-sm sm:text-base leading-snug"
        style={{ color: '#000150', fontFamily: "'Poppins', 'Inter', sans-serif" }}
      >
        {question}
      </span>
      <span
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300"
        style={{
          backgroundColor: isOpen ? '#07B0E9' : '#EBF8FE',
          transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
        }}
      >
        <svg className="w-4 h-4" style={{ color: isOpen ? '#fff' : '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
        </svg>
      </span>
    </button>
    {isOpen && (
      <div className="px-5 sm:px-7 pb-6 -mt-1">
        <p className="text-gray-600 text-sm leading-relaxed">{answer}</p>
      </div>
    )}
  </div>
);

const DallasServiceAreaPage = () => {
  const bgRef = useRef(null);
  const cardRef = useRef(null);
  const [cardVisible, setCardVisible] = useState(false);
  const [ticksVisible, setTicksVisible] = useState([false, false, false]);
  const [isMobile, setIsMobile] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  // Detect mobile on mount
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Parallax effect — disabled on mobile for performance
  useEffect(() => {
    if (isMobile) {
      if (bgRef?.current) bgRef.current.style.transform = 'none';
      return;
    }
    const handleScroll = () => {
      if (bgRef?.current) {
        const scrollY = window.scrollY;
        bgRef.current.style.transform = `translateY(${scrollY * 0.3}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // Fade-in slide-up animation for card on mount
  useEffect(() => {
    const timer = setTimeout(() => setCardVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Staggered animated tick icons
  useEffect(() => {
    if (!cardVisible) return;
    [0, 1, 2]?.forEach((i) => {
      setTimeout(() => {
        setTicksVisible((prev) => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
      }, 600 + i * 180);
    });
  }, [cardVisible]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F9FA', fontFamily: "'Poppins', 'Inter', sans-serif" }}>
      <Helmet>
        <title>Top Rated Dallas Roofing Companies | Transparent Online Roof Prices</title>
        <meta
          name="description"
          content="OpenbooQ brings transparent, GAF-exclusive roof replacement to Dallas and the DFW Metroplex. Exact quote texted and emailed in about 30 minutes, $0 down, open-book pricing. Licensed in Texas."
        />
        <meta name="keywords" content="dallas roofing companies, roofing companies dallas, dallas texas roofing, roof replacement dallas, dfw roofing, gaf roofing dallas, plano roofing, frisco roofing" />
        <script type="application/ld+json">
          {JSON.stringify(buildCityService({
            name: 'Dallas',
            region: 'TX',
            url: CANONICAL_URL,
            description: DALLAS_SERVICE_DESCRIPTION,
            offer: DALLAS_OFFER,
          }))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(buildBreadcrumb([
            { name: 'Home', item: 'https://openbooq.com' },
            { name: 'Service Areas', item: 'https://openbooq.com/service-areas' },
            { name: 'Dallas, TX', item: CANONICAL_URL },
          ]))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(buildFaqPage(DALLAS_FAQS))}
        </script>
      </Helmet>
      <Header />
      {/* ===== HERO SECTION ===== */}
      <section
        className="relative min-h-screen flex items-center overflow-hidden"
        style={{ backgroundColor: '#000150', paddingTop: '80px' }}
      >
        {/* Background image with parallax - full bleed */}
        {/* Iconic dusk Dallas skyline WITH Reunion Tower's lit ball (CEO: the
            ball must always be visible, incl. mobile). Public domain (Library
            of Congress via Wikimedia Commons), 1920w. backgroundPosition is
            tuned (31% x) so the ball lands in the clear right-of-text channel on
            narrow (390px) cover-crops keep it in view. */}
        <div
          ref={bgRef}
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/assets/images/dallas-reunion-skyline-dusk-publicdomain.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: '31% 28%',
            backgroundRepeat: 'no-repeat',
            top: '-10%',
            bottom: '-10%',
            willChange: isMobile ? 'auto' : 'transform',
          }}
        />
        {/* Full-bleed dark gradient overlay — bottom-heavy for text legibility */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, rgba(0,1,80,0.82) 0%, rgba(0,1,80,0.65) 40%, rgba(0,1,80,0.45) 70%, rgba(0,0,0,0.55) 100%)',
          }}
        />
        {/* Subtle grid */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="hero-grid" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#07B0E9" strokeWidth="0.8" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hero-grid)" />
          </svg>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Left: Headline + Trust Badges */}
            <div>
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6"
                style={{ backgroundColor: 'rgba(7,176,233,0.15)', color: '#07B0E9', border: '1px solid rgba(7,176,233,0.3)' }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                Dallas, Texas — Our Texas Launch Market
              </div>

              {/* Larger, bolder headline with brand blue gradient highlight */}
              <h1
                className="text-4xl sm:text-5xl lg:text-7xl font-black text-white mb-6 leading-tight"
                style={{ fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.03em' }}
              >
                Roofing
                <br />
                <span
                  style={{
                    background: 'linear-gradient(90deg, #07B0E9 0%, #0ea5e9 50%, #38bdf8 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Reimagined
                </span>
                <br />
                <span className="text-white">for Dallas.</span>
              </h1>

              <p className="text-gray-300 text-base sm:text-xl mb-8 leading-relaxed max-w-xl">
                GAF materials. Triple-layer warranty. Open-book pricing. Skip the pushy sales reps — your exact, GAF-measured roof quote is texted and emailed to you in about 30 minutes.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-2 sm:gap-3 mb-8 sm:mb-10">
                <div
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full"
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                >
                  <svg className="w-5 h-5" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span className="text-white text-xs font-bold uppercase tracking-wider">GAF-Certified Installers</span>
                </div>
                <div
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full"
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                >
                  <svg className="w-5 h-5" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span className="text-white text-xs font-bold uppercase tracking-wider">Class 4 Hail Options</span>
                </div>
                <div
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full"
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                >
                  <svg className="w-5 h-5" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-white text-xs font-bold uppercase tracking-wider">30-Minute Roof Quote</span>
                </div>
              </div>
            </div>

            {/* Right: Glassmorphism Card with fade-in slide-up animation */}
            <div className="flex justify-center lg:justify-end">
              <div
                ref={cardRef}
                className="w-full max-w-md rounded-3xl p-5 sm:p-8"
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(24px)',
                  WebkitBackdropFilter: 'blur(24px)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  boxShadow: '0 24px 64px rgba(0,1,80,0.35), 0 4px 16px rgba(7,176,233,0.15), inset 0 1px 0 rgba(255,255,255,0.2)',
                  opacity: cardVisible ? 1 : 0,
                  transform: cardVisible ? 'translateY(0)' : 'translateY(32px)',
                  transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)',
                }}
              >
                <div className="text-center mb-5 sm:mb-6">
                  <div
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-3"
                    style={{ backgroundColor: 'rgba(7,176,233,0.2)', color: '#07B0E9', border: '1px solid rgba(7,176,233,0.35)' }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                    Free Transparent Quote
                  </div>
                  <h2
                    className="text-2xl sm:text-3xl font-black mb-2"
                    style={{ color: '#ffffff', fontFamily: "'Poppins', sans-serif", textShadow: '0 1px 4px rgba(0,0,0,0.2)', letterSpacing: '-0.02em', lineHeight: 1.15 }}
                  >
                    Get My{' '}
                    <span
                      style={{
                        background: 'linear-gradient(90deg, #07B0E9 0%, #38bdf8 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      Transparent Quote
                    </span>
                  </h2>
                  <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.95rem', letterSpacing: '0.02em' }}>Enter your Dallas address below</p>
                </div>

                <div className="space-y-3">
                  <AddressInputWithMapbox
                    placeholder="Enter your Dallas home address..."
                    buttonText="Get My Transparent Quote — FREE"
                    buttonId="diq-journey-9674cf04-4057-4e53-acbb-533aa94307ea"
                    microcopy={null}
                  />
                </div>

                <p
                  className="text-center text-xs mt-4"
                  style={{ color: 'rgba(255,255,255,0.55)', fontFamily: "'Inter', sans-serif" }}
                >
                  Free quote • No sales calls • Dallas & the DFW Metroplex
                </p>

                {/* Trust row with animated tick icons */}
                <div className="flex items-center justify-center gap-3 sm:gap-4 mt-5 pt-5 flex-wrap" style={{ borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                  {[
                    { label: '$0 down' },
                    { label: 'No sales calls' },
                    { label: '30 minutes' },
                  ]?.map((item, i) => (
                    <div key={item?.label} className="flex items-center gap-1.5">
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: ticksVisible?.[i] ? '#22C55E' : 'transparent',
                          border: ticksVisible?.[i] ? '2px solid #22C55E' : '2px solid rgba(255,255,255,0.3)',
                          transition: 'all 0.4s cubic-bezier(0.34,1.56,0.64,1)',
                          transform: ticksVisible?.[i] ? 'scale(1)' : 'scale(0.6)',
                          opacity: ticksVisible?.[i] ? 1 : 0.4,
                        }}
                      >
                        <svg
                          style={{
                            width: '10px',
                            height: '10px',
                            color: '#fff',
                            opacity: ticksVisible?.[i] ? 1 : 0,
                            transition: 'opacity 0.3s ease',
                            transitionDelay: ticksVisible?.[i] ? '0.1s' : '0s',
                          }}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>{item?.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ===== INSTANT ESTIMATE HERO SECTION ===== */}
      <InstantEstimateHero />
      {/* ===== BETTER WAY SECTION ===== */}
      <section className="py-24 px-4" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Text */}
            <div>
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6"
                style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
              >
                A Better Way
              </div>
              <h2
                className="text-3xl sm:text-4xl font-black mb-6 leading-tight"
                style={{ color: '#000150', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.01em' }}
              >
                The Modern Alternative to Traditional Dallas Roofing Companies
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-5">
                Let's face it: getting a new roof in Dallas usually means taking time off work, waiting for contractors to show up, and sitting through high-pressure sales pitches. We built OpenbooQ to fix that. As the most transparent among dallas roofing companies, we let you price, purchase, and schedule your roof entirely from the comfort of your home — and we're licensed in Texas with GAF-certified crews statewide.
              </p>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                Using our proprietary <strong style={{ color: '#033494' }}>SmartPick™ AI technology</strong>, we measure your roof with pinpoint accuracy via a GAF aerial report — then text and email you an exact, line-itemed quote in about 30 minutes. Every quote is open-book: GAF materials at wholesale cost, labor, and one clearly itemized <strong style={{ color: '#033494' }}>Service & Warranty fee</strong>. No lump sums, no hidden markup, no salesperson at your door.
              </p>

              <div className="flex flex-col gap-4">
                {[
                  { icon: '🛰️', title: 'GAF-Measured, Texted & Emailed in ~30 Minutes', desc: 'A GAF aerial measurement of your exact roof — your itemized quote is texted and emailed to you, no site visit needed.' },
                  { icon: '📖', title: 'Open-Book Pricing', desc: 'Materials at wholesale cost plus one transparent Service & Warranty fee. You see every number.' },
                  { icon: '🏠', title: '100% Online Process', desc: 'Price, purchase, and schedule your roof from your couch — anywhere in the DFW Metroplex.' },
                ]?.map((item) => (
                  <div key={item?.title} className="flex items-start gap-4">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 text-xl"
                      style={{ backgroundColor: '#EBF8FE' }}
                    >
                      {item?.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-sm mb-0.5" style={{ color: '#000150' }}>{item?.title}</h4>
                      <p className="text-gray-500 text-sm leading-relaxed">{item?.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: SmartPick AI Mockup */}
            <div className="flex justify-center">
              <div
                className="relative w-full max-w-lg rounded-2xl overflow-hidden"
                style={{ boxShadow: '0 24px 64px rgba(0,1,80,0.15)', borderRadius: '20px' }}
              >
                {/* Laptop frame mockup */}
                <div
                  className="rounded-2xl p-6"
                  style={{ background: 'linear-gradient(135deg, #000150 0%, #033494 100%)' }}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                    <div className="flex-1 mx-4 h-6 rounded-full bg-white/10 flex items-center px-3">
                      <span className="text-white/50 text-xs">openbooq.com/smartpick</span>
                    </div>
                  </div>
                  {/* Screen content */}
                  <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#F8F9FA' }}>
                    <img
                      src="/assets/images/roof_top-1768329177363.png"
                      alt="SmartPick AI technology measuring a Dallas Texas roof via satellite on a laptop screen"
                      className="w-full object-cover"
                      style={{ height: '220px' }}
                      loading="lazy"
                    />
                    <div className="p-4">
                      <div
                        className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg"
                        style={{ backgroundColor: '#EBF8FE' }}
                      >
                        <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#07B0E9' }} />
                        <span className="text-xs font-bold" style={{ color: '#07B0E9' }}>SmartPick™ AI — Measuring Roof...</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {['Roof Area', 'Pitch', 'Complexity']?.map((label, i) => (
                          <div key={label} className="bg-white rounded-lg p-2 text-center" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                            <div className="text-xs font-black" style={{ color: '#000150' }}>
                              {i === 0 ? '2,340 sq ft' : i === 1 ? '6/12' : 'Moderate'}
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">{label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Badge overlay */}
                <div
                  className="absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-bold"
                  style={{ backgroundColor: '#07B0E9', color: '#fff', boxShadow: '0 4px 12px rgba(7,176,233,0.4)' }}
                >
                  SmartPick™ AI
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ===== PREMIUM MATERIALS SECTION (GAF-EXCLUSIVE) ===== */}
      <section className="py-24 px-4" style={{ backgroundColor: '#F8F9FA' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-4">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5"
              style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
            >
              GAF Exclusively, Installed by Certified Local Crews
            </div>
            <h2
              className="text-3xl sm:text-4xl font-black mb-4"
              style={{ color: '#000150', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.01em' }}
            >
              A Full GAF System on Every Dallas Roof
            </h2>
            <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
              North Texas weather is unforgiving — intense heat, high winds, and spring hail. That's why OpenbooQ installs GAF products exclusively, fitted by GAF-certified crews, so every roof is a full, warrantable GAF system from North America's largest roofing manufacturer.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {[
              {
                name: 'Timberline HDZ',
                brand: 'GAF',
                tagline: "America's #1-Selling Shingle",
                desc: 'The most popular architectural shingle in the country. LayerLock technology and StainGuard Plus protection, with superior wind resistance for open North Texas exposures.',
                swatches: ['#2C2C2C', '#5A4A3A', '#7A6A5A', '#4A3A2A', '#8A7A6A'],
                image: '/assets/images/gaf-timberline-hdz-charcoal-house_1_-1768332752421.jpg',
                imageAlt: 'House with GAF Timberline HDZ shingles in charcoal, as installed by OpenbooQ in Dallas Texas',
                featured: true,
              },
              {
                name: 'Timberline UHDZ',
                brand: 'GAF',
                tagline: 'Ultra-Premium Dimensional Look',
                desc: "GAF's ultra-premium shingle with the industry's widest nailing zone and a deep, dimensional profile — for Dallas homeowners who want the top of the GAF line.",
                swatches: ['#3A3A4A', '#5A5A6A', '#7A6A5A', '#4A4A5A', '#6A5A4A'],
                image: '/assets/images/gaf-timberline-hdz-pewter-gray-house_1_-1768333532310.jpg',
                imageAlt: 'House with GAF Timberline architectural shingles in pewter gray',
              },
              {
                name: 'Timberline AS II',
                brand: 'GAF',
                tagline: 'Class 4 Impact Resistance',
                desc: 'Engineered for storm-prone markets like North Texas: UL 2218 Class 4 impact-rated shingles built to shrug off the hail that defines the DFW Metroplex.',
                swatches: ['#4A4A4A', '#6B6B6B', '#8B7355', '#5C5C5C', '#3D3D3D'],
                image: '/assets/images/gaf-timberline-hdz-weathered-wood-house_1_-1768333058569.jpg',
                imageAlt: 'House with GAF Timberline architectural shingles in weathered wood',
              },
            ]?.map((shingle) => (
              <div
                key={shingle?.name}
                className="bg-white rounded-2xl overflow-hidden flex flex-col relative"
                style={{
                  borderRadius: '12px',
                  boxShadow: shingle?.featured
                    ? '0 20px 48px rgba(7,176,233,0.15), 0 4px 16px rgba(0,1,80,0.08)'
                    : '0 4px 24px rgba(0,1,80,0.07), 0 1px 4px rgba(0,0,0,0.04)',
                  border: shingle?.featured ? '2px solid rgba(7,176,233,0.3)' : '1px solid rgba(0,0,0,0.04)',
                }}
              >
                {shingle?.featured && (
                  <div
                    className="absolute top-4 right-4 z-10 px-3 py-1 rounded-full text-xs font-bold"
                    style={{ backgroundColor: '#07B0E9', color: '#fff' }}
                  >
                    Most Popular
                  </div>
                )}
                <div className="relative overflow-hidden" style={{ height: '200px' }}>
                  <img
                    src={shingle?.image}
                    alt={shingle?.imageAlt}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div
                    className="absolute bottom-0 left-0 right-0 h-16"
                    style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.4), transparent)' }}
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="mb-3">
                    <span
                      className="text-xs font-bold uppercase tracking-widest px-2 py-1 rounded-full"
                      style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
                    >
                      {shingle?.brand}
                    </span>
                  </div>
                  <h3
                    className="text-xl font-black mb-1"
                    style={{ color: '#000150', fontFamily: "'Poppins', sans-serif" }}
                  >
                    {shingle?.name}
                  </h3>
                  <p className="text-xs font-semibold mb-3" style={{ color: '#07B0E9' }}>{shingle?.tagline}</p>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-1">{shingle?.desc}</p>

                  {/* Color swatches */}
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-xs text-gray-400 font-medium">Colors:</span>
                    <div className="flex gap-1.5">
                      {shingle?.swatches?.map((color, i) => (
                        <div
                          key={i}
                          className="w-5 h-5 rounded-full border-2 border-white cursor-pointer hover:scale-110 transition-transform"
                          style={{ backgroundColor: color, boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Feature bullets */}
                  <div className="flex flex-col gap-1.5 mb-5">
                    {['Lifetime Limited Materials Warranty', 'Full GAF System Components', 'GAF-Certified Installation']?.map((feat) => (
                      <div key={feat} className="flex items-center gap-2">
                        <svg className="w-4 h-4 flex-shrink-0" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-xs text-gray-600 font-medium">{feat}</span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/shingles"
                    className="block text-center py-3 px-6 rounded-full text-white font-bold text-sm transition-all duration-300 hover:opacity-90 hover:shadow-lg hover:scale-105"
                    style={{ backgroundColor: '#07B0E9', boxShadow: '0 4px 16px rgba(7,176,233,0.3)' }}
                  >
                    Select Product
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ===== DALLAS WEATHER / TRUST SECTION ===== */}
      <section className="py-24 px-4" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-5xl mx-auto">
          <div
            className="rounded-3xl p-10 sm:p-14 text-white text-center relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #07B0E9 0%, #033494 100%)',
              boxShadow: '0 24px 64px rgba(3,52,148,0.25)',
            }}
          >
            {/* Background pattern */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
              <svg className="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="weather-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <circle cx="20" cy="20" r="1" fill="white" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#weather-grid)" />
              </svg>
            </div>

            <div className="relative z-10">
              {/* Weather icons row */}
              <div className="flex items-center justify-center gap-6 sm:gap-8 mb-8 flex-wrap">
                {/* Hail */}
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-10 h-10 opacity-90" fill="none" stroke="white" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 12z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19l-1 2m4-2l-1 2m4-2l-1 2" />
                  </svg>
                  <span className="text-xs font-bold uppercase tracking-wider opacity-80">Spring Hail</span>
                </div>
                {/* Wind */}
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-10 h-10 opacity-90" fill="none" stroke="white" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.59 4.59A2 2 0 004 12h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 12z" />
                  </svg>
                  <span className="text-xs font-bold uppercase tracking-wider opacity-80">High Winds</span>
                </div>
                {/* Storm */}
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-10 h-10 opacity-90" fill="none" stroke="white" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span className="text-xs font-bold uppercase tracking-wider opacity-80">Severe Storms</span>
                </div>
                {/* Sun */}
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-10 h-10 opacity-90" fill="none" stroke="white" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
                  </svg>
                  <span className="text-xs font-bold uppercase tracking-wider opacity-80">Texas Heat</span>
                </div>
              </div>

              <h2
                className="text-3xl sm:text-4xl font-black mb-5 leading-tight"
                style={{ fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.01em' }}
              >
                Built for North Texas Hail Alley.
                <br />
                <span className="opacity-90">Every Season. Every Storm.</span>
              </h2>
              <p className="text-white/80 text-lg max-w-2xl mx-auto leading-relaxed mb-8">
                Dallas sits where warm Gulf air collides with cold fronts — the stretch of North Texas known as Hail Alley. From spring hailstorms to triple-digit summer heat and high straight-line winds, your roof takes a beating year-round. That's why every OpenbooQ roof is a full GAF system installed by GAF-certified crews, with a Class 4 impact-rated option (GAF Timberline AS II) built for exactly this climate.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                {[
                  { stat: 'Class 4', label: 'Impact-Rated Option (UL 2218)' },
                  { stat: 'GAF', label: 'Full System, Certified Crews' },
                  { stat: 'Lifetime', label: 'Limited Materials Warranty' },
                ]?.map((item) => (
                  <div
                    key={item?.label}
                    className="rounded-xl p-4"
                    style={{ backgroundColor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)' }}
                  >
                    <div className="text-2xl font-black mb-1">{item?.stat}</div>
                    <div className="text-xs opacity-75 font-medium uppercase tracking-wider">{item?.label}</div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => {
                  openCustomerPortal();
                }}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-sm transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                style={{ backgroundColor: '#fff', color: '#033494', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}
              >
                Get My Storm-Ready Roof Quote
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>
      {/* ===== TRIPLE-LAYER WARRANTY SECTION ===== */}
      <section className="py-24 px-4" style={{ backgroundColor: '#F8F9FA' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5"
              style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
            >
              Triple-Layer Protection
            </div>
            <h2
              className="text-3xl sm:text-4xl font-black mb-4"
              style={{ color: '#000150', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.01em' }}
            >
              Three Layers of Warranty on Every Dallas Roof.
            </h2>
            <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
              Your quote's Service & Warranty fee isn't a charge that disappears into overhead — it's what stands behind your roof after the crew leaves. Every OpenbooQ roof in Dallas carries three layers of protection.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: '🏭',
                title: 'Lifetime Materials',
                subtitle: 'GAF Manufacturer Warranty',
                desc: 'Every roof is a full GAF system — shingles, starter strips, ridge cap, and underlayment — backed by GAF\'s lifetime limited materials warranty from North America\'s largest roofing manufacturer.',
                highlight: 'Layer 1',
                highlightColor: '#07B0E9',
              },
              {
                icon: '🛠️',
                title: '5-Year Workmanship',
                subtitle: 'OpenbooQ Install Warranty',
                desc: 'The installation itself is covered by OpenbooQ\'s 5-year workmanship warranty. GAF-certified crews, an assigned project manager, and body cameras worn for the entire job.',
                highlight: 'Layer 2',
                highlightColor: '#033494',
                featured: true,
              },
              {
                icon: '✅',
                title: 'Satisfaction Guarantee',
                subtitle: 'Inspect Before You Pay',
                desc: 'You pay $0 down and nothing until the roof is on. Then you get a 7-day inspection window to walk the finished work — payment happens only after you\'re satisfied.',
                highlight: 'Layer 3',
                highlightColor: '#22C55E',
              },
            ]?.map((layer) => (
              <div
                key={layer?.title}
                className="bg-white rounded-2xl p-8 flex flex-col"
                style={{
                  borderRadius: '12px',
                  boxShadow: layer?.featured
                    ? '0 20px 48px rgba(7,176,233,0.15), 0 4px 16px rgba(0,1,80,0.08)'
                    : '0 4px 24px rgba(0,1,80,0.07), 0 1px 4px rgba(0,0,0,0.04)',
                  border: layer?.featured ? '2px solid rgba(7,176,233,0.3)' : '1px solid rgba(0,0,0,0.04)',
                }}
              >
                <div className="text-3xl mb-4">{layer?.icon}</div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-black" style={{ color: '#000150', fontFamily: "'Poppins', sans-serif" }}>
                    {layer?.title}
                  </h3>
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: layer?.highlightColor + '20', color: layer?.highlightColor }}
                  >
                    {layer?.highlight}
                  </span>
                </div>
                <p className="text-xs font-semibold mb-3" style={{ color: '#07B0E9' }}>{layer?.subtitle}</p>
                <p className="text-gray-500 text-sm leading-relaxed flex-1 mb-6">{layer?.desc}</p>
                <Link
                  to="/warranty"
                  className="block text-center py-3 px-6 rounded-full font-bold text-sm transition-all duration-300 hover:opacity-90 hover:shadow-lg"
                  style={{
                    backgroundColor: layer?.featured ? '#07B0E9' : 'transparent',
                    color: layer?.featured ? '#fff' : '#033494',
                    border: layer?.featured ? 'none' : '2px solid #033494',
                  }}
                >
                  Warranty Details
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ===== FINANCING SECTION ===== */}
      <section className="py-24 px-4" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5"
              style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
            >
              Flexible Payment Options
            </div>
            <h2
              className="text-3xl sm:text-4xl font-black mb-4"
              style={{ color: '#000150', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.01em' }}
            >
              $0 Down. No Hidden Fees. No Surprises.
            </h2>
            <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
              Every option starts the same way: $0 down, and you pay after installation and your 7-day inspection window. Pay upfront to lock in the lowest price, use secure escrow, or finance monthly through Enhancify — you're always in control, with open-book pricing from quote to installation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: '💵',
                title: 'Pay After Install',
                subtitle: 'Lowest Price Guaranteed',
                desc: 'Pay in full only after your roof is installed and your 7-day inspection window closes. No financing fees, no interest — just the most transparent roofing deal in Dallas.',
                highlight: 'Best Price',
                highlightColor: '#22C55E',
                cta: 'Get My Price',
              },
              {
                icon: '🔒',
                title: 'Escrow Protection',
                subtitle: 'Secure & Protected',
                desc: 'Your funds are held securely in escrow and only released upon your satisfaction. Complete peace of mind from day one to final walkthrough.',
                highlight: 'Most Secure',
                highlightColor: '#07B0E9',
                cta: 'Learn About Escrow',
                featured: true,
              },
              {
                icon: '📅',
                title: 'Monthly Financing',
                subtitle: 'Through Enhancify',
                desc: 'Spread the cost of your new GAF roof over time with competitive financing through Enhancify. Get the roof your home needs today without breaking the bank.',
                highlight: 'Most Flexible',
                highlightColor: '#F59E0B',
                cta: 'See Financing Options',
              },
            ]?.map((option) => (
              <div
                key={option?.title}
                className="bg-white rounded-2xl p-8 flex flex-col"
                style={{
                  borderRadius: '12px',
                  boxShadow: option?.featured
                    ? '0 20px 48px rgba(7,176,233,0.15), 0 4px 16px rgba(0,1,80,0.08)'
                    : '0 4px 24px rgba(0,1,80,0.07), 0 1px 4px rgba(0,0,0,0.04)',
                  border: option?.featured ? '2px solid rgba(7,176,233,0.3)' : '1px solid rgba(0,0,0,0.04)',
                }}
              >
                <div className="text-3xl mb-4">{option?.icon}</div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-black" style={{ color: '#000150', fontFamily: "'Poppins', sans-serif" }}>
                    {option?.title}
                  </h3>
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: option?.highlightColor + '20', color: option?.highlightColor }}
                  >
                    {option?.highlight}
                  </span>
                </div>
                <p className="text-xs font-semibold mb-3" style={{ color: '#07B0E9' }}>{option?.subtitle}</p>
                <p className="text-gray-500 text-sm leading-relaxed flex-1 mb-6">{option?.desc}</p>
                <Link
                  to="/financing-options"
                  className="block text-center py-3 px-6 rounded-full font-bold text-sm transition-all duration-300 hover:opacity-90 hover:shadow-lg"
                  style={{
                    backgroundColor: option?.featured ? '#07B0E9' : 'transparent',
                    color: option?.featured ? '#fff' : '#033494',
                    border: option?.featured ? 'none' : '2px solid #033494',
                  }}
                >
                  {option?.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ===== NEIGHBORHOODS + FAQ SECTION ===== */}
      <section className="py-24 px-4" style={{ backgroundColor: '#F8F9FA' }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5"
              style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
            >
              Dallas Roofing Questions
            </div>
            <h2
              className="text-3xl sm:text-4xl font-black mb-4"
              style={{ color: '#000150', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.01em' }}
            >
              Dallas Homeowners Ask, We Answer.
            </h2>
            <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
              From Oak Cliff and Lakewood to Lake Highlands, Preston Hollow, the M Streets, Richardson and Garland — and across Plano, Frisco, McKinney, Arlington and Fort Worth — here's what DFW homeowners want to know before buying a roof online.
            </p>
          </div>

          <div className="flex flex-col gap-4">
            {DALLAS_FAQS?.map((faq, i) => (
              <FaqItem
                key={faq?.question}
                question={faq?.question}
                answer={faq?.answer}
                isOpen={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
              />
            ))}
          </div>

          {/* Internal links to the authoritative hubs */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 mt-10 text-sm">
            <Link to="/pricing" className="font-semibold hover:underline" style={{ color: '#033494' }}>
              How OpenbooQ pricing works
            </Link>
            <a href="/roof-prices/texas/dallas/" className="font-semibold hover:underline" style={{ color: '#033494' }}>
              Dallas roof cost data
            </a>
            <a href="/roofing/texas/dallas/" className="font-semibold hover:underline" style={{ color: '#033494' }}>
              Dallas coverage details
            </a>
            <Link to="/warranty" className="font-semibold hover:underline" style={{ color: '#033494' }}>
              Warranty coverage
            </Link>
            <Link to="/service-areas" className="font-semibold hover:underline" style={{ color: '#033494' }}>
              All service areas
            </Link>
          </div>
        </div>
      </section>
      {/* ===== BOTTOM CTA SECTION ===== */}
      <section
        className="py-24 px-4"
        style={{ backgroundColor: '#000150' }}
      >
        <div className="max-w-3xl mx-auto text-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6"
            style={{ backgroundColor: 'rgba(7,176,233,0.15)', color: '#07B0E9', border: '1px solid rgba(7,176,233,0.3)' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
            Start Your Transparent Quote
          </div>
          <h2
            className="text-3xl sm:text-4xl font-black text-white mb-5 leading-tight"
            style={{ fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.01em' }}
          >
            Ready to Get Your Exact Dallas Roof Price?
          </h2>
          <p className="text-gray-400 text-lg mb-10 leading-relaxed">
            Dallas is where OpenbooQ's Texas story starts. No sales calls. No pressure. $0 down. Just your exact, GAF-measured quote texted and emailed to you in about 30 minutes.
          </p>

          <div className="max-w-xl mx-auto">
            <AddressInputWithMapbox
              placeholder="Enter your Dallas home address..."
              buttonText="Get My Transparent Quote — FREE"
              buttonId="diq-journey-9674cf04-4057-4e53-acbb-533aa94307ea-bottom"
              microcopy="Free quote • No sales calls • Dallas & the DFW Metroplex"
            />
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 mt-10">
            {[
              { icon: '⚡', text: 'Quote texted & emailed in ~30 minutes' },
              { icon: '🚫', text: 'No sales calls ever' },
              { icon: '📖', text: 'Open-book pricing' },
              { icon: '🛡️', text: 'Triple-layer warranty' },
            ]?.map((item) => (
              <div key={item?.text} className="flex items-center gap-2">
                <span className="text-base">{item?.icon}</span>
                <span className="text-gray-400 text-sm font-medium">{item?.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default DallasServiceAreaPage;
